import type { InfoChartKind, InfoChartPoint } from './workspace.types';

/**
 * Dependency-free SVG chart for the workspace info feed: `line` for results
 * over time (labs, vitals), `bar` for comparisons. Scales with its container
 * via viewBox; static by design (no animation). An optional reference band
 * shades the normal range so out-of-range points read at a glance.
 */

const VIEW_W = 560;
const VIEW_H = 236;
const PAD_LEFT = 48;
const PAD_RIGHT = 14;
const PAD_TOP = 22;
const PAD_BOTTOM = 34;
const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
/** Beyond this many points, thin the x-axis / value labels to stay legible. */
const MAX_LABELS = 8;

const formatValue = (value: number): string => {
  const abs = Math.abs(value);
  const rounded =
    abs >= 100 ? Math.round(value) : Number(value.toFixed(abs >= 10 ? 1 : 2));
  return `${rounded}`;
};

const truncateLabel = (label: string): string =>
  label.length > 12 ? `${label.slice(0, 11)}…` : label;

interface InfoChartProps {
  chartKind: InfoChartKind;
  series: InfoChartPoint[];
  unit?: string;
  referenceLow?: number;
  referenceHigh?: number;
  referenceLabel?: string;
  /** Spoken equivalent of the visual — the series read out. */
  ariaLabel: string;
}

export function InfoChart({
  chartKind,
  series,
  unit,
  referenceLow,
  referenceHigh,
  referenceLabel,
  ariaLabel,
}: InfoChartProps) {
  const values = series.map((point) => point.value);
  const domainValues = [
    ...values,
    ...(referenceLow !== undefined ? [referenceLow] : []),
    ...(referenceHigh !== undefined ? [referenceHigh] : []),
    // Bars read as magnitude from zero.
    ...(chartKind === 'bar' ? [0] : []),
  ];
  let min = Math.min(...domainValues);
  let max = Math.max(...domainValues);
  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.1);
    min -= pad;
    max += pad;
  } else {
    const pad = (max - min) * 0.08;
    min -= chartKind === 'bar' && min === 0 ? 0 : pad;
    max += pad;
  }

  const y = (value: number) => PAD_TOP + (1 - (value - min) / (max - min)) * PLOT_H;
  const slot = PLOT_W / series.length;
  const x = (index: number) => PAD_LEFT + slot * (index + 0.5);

  const ticks = [0, 1, 2, 3].map((i) => min + ((max - min) * i) / 3);
  const labelStep = Math.max(1, Math.ceil(series.length / MAX_LABELS));
  const showLabel = (index: number) =>
    index % labelStep === 0 || index === series.length - 1;

  const bandTop = referenceHigh !== undefined ? y(Math.min(referenceHigh, max)) : null;
  const bandBottom = referenceLow !== undefined ? y(Math.max(referenceLow, min)) : null;

  const barWidth = Math.min(48, slot * 0.56);
  const zeroY = y(Math.max(min, Math.min(max, 0)));

  const linePoints = series
    .map((point, index) => `${x(index)},${y(point.value)}`)
    .join(' ');
  const areaPoints = `${PAD_LEFT + slot * 0.5},${PAD_TOP + PLOT_H} ${linePoints} ${
    PAD_LEFT + slot * (series.length - 0.5)
  },${PAD_TOP + PLOT_H}`;

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="h-auto w-full tabular-nums"
      role="img"
      aria-label={ariaLabel}
    >
      {/* Grid + y-axis labels */}
      <g className="text-text-tertiary">
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD_LEFT}
              x2={VIEW_W - PAD_RIGHT}
              y1={y(tick)}
              y2={y(tick)}
              stroke="currentColor"
              strokeOpacity={0.2}
            />
            <text
              x={PAD_LEFT - 8}
              y={y(tick) + 3.5}
              textAnchor="end"
              fontSize={10.5}
              fill="currentColor"
            >
              {formatValue(tick)}
            </text>
          </g>
        ))}
      </g>

      {/* Reference range band */}
      {bandTop !== null || bandBottom !== null ? (
        <g className="text-green-600">
          <rect
            x={PAD_LEFT}
            width={PLOT_W}
            y={bandTop ?? PAD_TOP}
            height={Math.max(0, (bandBottom ?? PAD_TOP + PLOT_H) - (bandTop ?? PAD_TOP))}
            fill="currentColor"
            fillOpacity={0.07}
          />
          {[bandTop, bandBottom]
            .filter((edge): edge is number => edge !== null)
            .map((edge) => (
              <line
                key={edge}
                x1={PAD_LEFT}
                x2={VIEW_W - PAD_RIGHT}
                y1={edge}
                y2={edge}
                stroke="currentColor"
                strokeOpacity={0.4}
                strokeDasharray="4 4"
              />
            ))}
          {referenceLabel ? (
            <text
              x={VIEW_W - PAD_RIGHT - 4}
              y={(bandTop ?? bandBottom ?? PAD_TOP) - 5}
              textAnchor="end"
              fontSize={10}
              fill="currentColor"
            >
              {referenceLabel}
            </text>
          ) : null}
        </g>
      ) : null}

      {/* Data */}
      {chartKind === 'line' ? (
        <g className="text-text-primary">
          <polygon points={areaPoints} fill="currentColor" fillOpacity={0.05} />
          <polyline
            points={linePoints}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {series.map((point, index) => (
            <g key={`${point.label}-${index}`}>
              <circle cx={x(index)} cy={y(point.value)} r={3.5} fill="currentColor" />
              {showLabel(index) ? (
                <text
                  x={x(index)}
                  y={y(point.value) - 9}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="currentColor"
                >
                  {formatValue(point.value)}
                </text>
              ) : null}
            </g>
          ))}
        </g>
      ) : (
        <g className="text-text-primary">
          {series.map((point, index) => {
            const top = Math.min(y(point.value), zeroY);
            const height = Math.max(1.5, Math.abs(y(point.value) - zeroY));
            return (
              <g key={`${point.label}-${index}`}>
                <rect
                  x={x(index) - barWidth / 2}
                  y={top}
                  width={barWidth}
                  height={height}
                  rx={4}
                  fill="currentColor"
                  fillOpacity={0.85}
                />
                {showLabel(index) ? (
                  <text
                    x={x(index)}
                    y={top - 6}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={600}
                    fill="currentColor"
                  >
                    {formatValue(point.value)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </g>
      )}

      {/* X-axis labels */}
      <g className="text-text-tertiary">
        {series.map((point, index) =>
          showLabel(index) ? (
            <text
              key={`${point.label}-${index}`}
              x={x(index)}
              y={VIEW_H - PAD_BOTTOM + 18}
              textAnchor="middle"
              fontSize={10.5}
              fill="currentColor"
            >
              {truncateLabel(point.label)}
            </text>
          ) : null
        )}
        {unit ? (
          <text
            x={PAD_LEFT - 8}
            y={12}
            textAnchor="end"
            fontSize={10}
            fill="currentColor"
          >
            {unit}
          </text>
        ) : null}
      </g>
    </svg>
  );
}
